import { Controller } from "@hotwired/stimulus"
import { canControlVolume, formatDuration, loopPosition, safeHttpsUrl } from "../lib/ig_music.js"

// 予約投稿フォーム内の「音楽」欄。選択結果は hidden(audio_*)に入れる。
// 楽曲の選択はフォーム外のモーダル(ig-music-picker)とイベントでやり取りする。
// 「動画と合わせて試聴」は <video> と <audio> を同時再生する目安(完成形のプレビュー API は無い)
export default class extends Controller {
  static targets = [
    "emptyState", "card", "artwork", "artworkFallback", "title", "artist", "duration", "warning",
    "audioIdInput", "titleInput", "artistInput", "durationInput",
    "audioVolume", "videoVolume", "audioVolumeLabel", "videoVolumeLabel",
    "previewButton", "previewReason", "previewPanel", "video", "audio", "volumeNote"
  ]
  static values = { previewUrl: String, videoUrl: String }

  connect() {
    this.track = null
    this.objectUrl = null
    this.videoSrc = this.videoUrlValue || null
    this.syncing = false
    this.updateVolumeLabels()
    this.render()
    if (this.audioIdInputTarget.value) this.refreshTrack(this.audioIdInputTarget.value)
  }

  disconnect() {
    this.stopSyncPreview()
    this.revokeObjectUrl()
  }

  openPicker() {
    this.dispatch("open-picker")
  }

  // ig-music-picker:select
  select(event) {
    const track = event.detail?.track
    if (!track) return
    this.stopSyncPreview()
    this.track = track
    this.audioIdInputTarget.value = track.audio_id
    this.titleInputTarget.value = track.title || ""
    this.artistInputTarget.value = track.artist || ""
    this.durationInputTarget.value = Number.isFinite(track.duration_ms) ? String(track.duration_ms) : ""
    this.showWarning(null)
    this.render()
    // hidden を書き換えた後に知らせる(右プレビューが「楽曲選択中」の注記を出し直す)
    this.dispatch("audio-changed")
  }

  remove() {
    this.stopSyncPreview()
    this.track = null
    ;[ this.audioIdInputTarget, this.titleInputTarget, this.artistInputTarget, this.durationInputTarget ]
      .forEach((input) => { input.value = "" })
    this.showWarning(null)
    this.render()
    this.dispatch("audio-changed")
  }

  // scheduled-post:post-type-changed
  postTypeChanged(event) {
    const reels = event.detail?.postType === "reels"
    this.element.classList.toggle("hidden", !reels)
    if (!reels && this.audioIdInputTarget.value) this.remove()
  }

  // scheduled-post:video-selected / video-cleared(曲は動画に依存しないので外さない)
  videoSelected(event) {
    const file = event.detail?.file
    if (!file) return
    this.stopSyncPreview()
    this.revokeObjectUrl()
    this.objectUrl = URL.createObjectURL(file)
    this.videoSrc = this.objectUrl
    this.render()
  }

  videoCleared() {
    this.stopSyncPreview()
    this.revokeObjectUrl()
    this.videoSrc = null
    this.render()
  }

  async refreshTrack(audioId) {
    try {
      const url = new URL(this.previewUrlValue, window.location.origin)
      url.searchParams.set("audio_id", audioId)
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await response.json()
      // 応答を待つ間に曲を選び直した・外した場合は古い応答を捨てる
      if (this.audioIdInputTarget.value !== audioId) return
      if (data.status === "ok" && data.track) {
        this.track = data.track
        this.render()
      } else if (data.status === "not_found") {
        this.showWarning("この楽曲は現在利用できません。別の曲を選んでください")
      } else {
        this.showWarning("試聴情報を取得できませんでした")
      }
    } catch {
      if (this.audioIdInputTarget.value !== audioId) return
      this.showWarning("試聴情報を取得できませんでした")
    }
  }

  showWarning(message) {
    this.warningTarget.textContent = message || ""
    this.warningTarget.classList.toggle("hidden", !message)
  }

  render() {
    const audioId = this.audioIdInputTarget.value
    const selected = Boolean(audioId)
    this.emptyStateTarget.classList.toggle("hidden", selected)
    this.cardTarget.classList.toggle("hidden", !selected)
    if (!selected) return

    this.titleTarget.textContent = this.track?.title || this.titleInputTarget.value || `楽曲ID ${audioId}`
    const artist = this.track?.artist || this.artistInputTarget.value
    const durationMs = this.track?.duration_ms ?? Number(this.durationInputTarget.value || NaN)
    const duration = formatDuration(durationMs)
    this.artistTarget.textContent = artist
    // 区切りは「 ・ 」。flex の子の先頭の空白は詰められるため、ノーブレークスペースで挟む
    this.durationTarget.textContent = artist && duration ? `\u00a0・\u00a0${duration}` : duration

    const artworkUrl = safeHttpsUrl(this.track?.artwork_url)
    this.artworkTarget.classList.toggle("hidden", !artworkUrl)
    this.artworkFallbackTarget.classList.toggle("hidden", Boolean(artworkUrl))
    if (artworkUrl) this.artworkTarget.src = artworkUrl

    const previewUrl = safeHttpsUrl(this.track?.preview_url)
    const reason = !previewUrl ? "この楽曲は試聴できません" : (!this.videoSrc ? "動画を選択すると試聴できます" : "")
    this.previewButtonTarget.disabled = Boolean(reason)
    this.previewButtonTarget.title = reason
    // 押せない理由は title(ホバー)だけでは伝わらないため、ボタンの下にも出す
    if (this.hasPreviewReasonTarget) {
      this.previewReasonTarget.textContent = reason
      this.previewReasonTarget.classList.toggle("hidden", !reason)
    }
  }

  changeVolume() {
    this.updateVolumeLabels()
    this.applyVolumes()
  }

  updateVolumeLabels() {
    this.audioVolumeLabelTarget.textContent = this.audioVolumeTarget.value
    this.videoVolumeLabelTarget.textContent = this.videoVolumeTarget.value
  }

  applyVolumes() {
    try {
      this.videoTarget.volume = Number(this.videoVolumeTarget.value) / 100
      this.audioTarget.volume = Number(this.audioVolumeTarget.value) / 100
    } catch {
      // 音量を変えられない端末(iOS)は volumeNote で案内する
    }
  }

  toggleSyncPreview() {
    if (this.syncing) this.stopSyncPreview()
    else this.startSyncPreview()
  }

  startSyncPreview() {
    const previewUrl = safeHttpsUrl(this.track?.preview_url)
    if (!previewUrl || !this.videoSrc) return
    // 右プレビューで元の音声をオンにしていたらミュートに戻してもらう(同時に2つ鳴らさない)。
    // play() の成否は待たずに先に送る。拒否されてもこのパネルは閉じ、右はミュートのまま=どちらも鳴っていない状態に揃う
    this.dispatch("sync-preview-started")
    this.syncing = true
    this.previewPanelTarget.classList.remove("hidden")
    this.volumeNoteTarget.classList.toggle("hidden", canControlVolume(this.audioTarget))
    this.videoTarget.src = this.videoSrc
    this.audioTarget.src = previewUrl
    this.applyVolumes()
    this.videoTarget.currentTime = 0
    this.audioTarget.currentTime = 0
    this.videoTarget.play().catch(() => this.stopSyncPreview())
    // iOS はユーザー操作の中で呼んだ play() しか音を出さないため、audio もボタン操作の中で直接再生する
    // (位置合わせは video の play イベント → syncPlay で行う)
    this.audioTarget.play().catch(() => {})
    this.previewButtonTarget.textContent = "試聴を止める"
  }

  // scheduled-post:preview-sound-on(右プレビューで元の音声をオンにしたとき)からも呼ばれる。パネルが閉じていても冪等
  stopSyncPreview() {
    if (!this.hasVideoTarget) return
    this.syncing = false
    this.videoTarget.pause()
    this.audioTarget.pause()
    this.previewPanelTarget.classList.add("hidden")
    this.previewButtonTarget.textContent = "動画と合わせて試聴"
  }

  // <video> の play / pause / seeked / ended
  syncPlay() {
    if (!this.syncing) return
    this.audioTarget.currentTime = loopPosition(this.videoTarget.currentTime, this.audioTarget.duration)
    this.audioTarget.play().catch(() => {})
  }

  syncPause() {
    this.audioTarget.pause()
  }

  syncSeek() {
    if (!this.syncing) return
    this.audioTarget.currentTime = loopPosition(this.videoTarget.currentTime, this.audioTarget.duration)
  }

  revokeObjectUrl() {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = null
  }
}
